import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { getPickById } from 'services/picksApi';
import { listGames } from 'services/gamesApi';
import { RISK_LABEL, formatAmericanOdds, formatDate } from 'utils';
import type { ApiPick, ApiGame, ApiGameListResponse, PicksFilterValues } from 'services/types';
import LockPickTM from 'components/LockPickTM/LockPickTM';
import Input from 'components/Input/Input';
import PickLeg from 'components/PickLeg/PickLeg';
import Spinner from 'components/Spinner/Spinner';
import { t } from '../../helpers/componentHelper';

import './PickDetail.scss';

type ApiPickDetail = ApiPick & {
  gameName: string;
  gameStart: string;
};

export type PickDetailGameInfo = {
  eventId: string;
  eventName: string;
  eventStart: string;
};

function PickDetail() {
  const navigate = useNavigate();
  const location = useLocation();
  const { pickId } = useParams<{ pickId: string }>();
  const state = location?.state as { currentFilters?: PicksFilterValues } | undefined;
  const [pick, setPick] = useState<ApiPickDetail | null>(null);
  const [gameById, setGameById] = useState<Record<string, PickDetailGameInfo>>({});
  const [betAmount, setBetAmount] = useState<number>(100);
  const [payoutAmount, setPayoutAmount] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (pick?.sport) {
      let cancelled = false;
      setLoading(true);
      listGames({ sport: pick.sport, pageNum: 0 })
        .then((res: ApiGameListResponse) => {
          if (cancelled) return;
          const gameById: Record<string, PickDetailGameInfo> = {};
          res.results.forEach((game: ApiGame) => {
            gameById[game.eventId] = {
              eventId: game.eventId,
              eventName: game.eventName.replace('Athletics Athletics', 'Athletics'),
              eventStart: game.scheduledAt,
            };
          });
          setGameById({ ...gameById });
        })
        .catch((err) => {
          if (cancelled) return;
          const message =
            err?.response?.data?.error?.message ?? err?.message ?? 'Failed to load games';
          setError(String(message));
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
      return () => {
        cancelled = true;
      };
    }
  }, [pick?.sport]);

  const pickUpdate = (cancelled: boolean) => {
    getPickById(pickId as string)
      .then((res: ApiPick) => {
        if (cancelled) return;
        const pick: ApiPickDetail = { ...res, gameName: '', gameStart: '' };
        pick.gameName = gameById[res.eventId]?.eventName ?? res.eventId; // fallback to ID if name is missing
        pick.gameStart = gameById[res.eventId]?.eventStart ?? '';
        setPick(pick);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    pickUpdate(cancelled);
    return () => {
      cancelled = true;
    };
  }, [pickId, gameById]);

  // calculate base payout of $100 bet
  useEffect(() => {
    const id = setTimeout(() => {
      onBetChangeHandler(100);
      clearTimeout(id);
    }, 10);
  }, [pick?.parlayAmericanOdds]);

  const onBetChangeHandler = (betAmount: number) => {
    let amount = 0;
    if (betAmount >= 0 && pick?.parlayAmericanOdds && betAmount <= 10000000) {
      const odds = pick?.parlayAmericanOdds;
      if (odds < 0) {
        amount = Math.round((100 / (odds * -1) + 1) * betAmount);
      } else if (odds > 0) {
        amount = Math.round((odds / 100 + 1) * betAmount);
      }
      setBetAmount(betAmount);
    } else {
      setBetAmount(0);
    }
    setPayoutAmount(amount);
  };

  const handleBack = useCallback(() => {
    navigate('/picks', {
      state: { currentFilters: state?.currentFilters ?? null },
    });
  }, []);

  const computedDateString = pick?.computedDate ? formatDate(pick.computedDate) : '';
  const detailClass =
    pick && pick.riskLevel === 'lock'
      ? '--lock'
      : pick && pick.riskLevel === 'fifty_fifty'
      ? '--fifty_fifty'
      : pick && pick.riskLevel === 'risky'
      ? '--risky'
      : '';
  return (
    <div className={`pick-detail pick-detail${detailClass}`}>
      <div className={'pick-detail__header'}>
        <span className={'pick-detail__back'} onClick={() => handleBack()}>
          Return to picks
        </span>
        <div className={'pick-detail__header-container'}>
          <div className={'pick-detail__header-left'}>
            {pick && (
              <span className={`pick-detail__risk pick-detail__risk--${pick.riskLevel}`}>
                {pick.riskLevel === 'lock' ? <LockPickTM /> : RISK_LABEL[pick.riskLevel]}
              </span>
            )}
          </div>
          <div className={'pick-detail__header-right'}>
            {pick && (
              <>
                <span className={'pick-detail__odds'}>
                  <span className={'odds-value'}>
                    EQ Odds {formatAmericanOdds(pick.parlayAmericanOdds)}
                  </span>
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      {loading && (
        <div className={'pick-detail__loading'}>
          <Spinner />
        </div>
      )}
      {error && <div className={'pick-detail__error'}>{error}</div>}

      <div className={'pick-detail__content'}>
        {pick && (
          <>
            <div className={'pick-detail__body-header'}>
              {pick && (
                <a
                  href={`https://www.espn.com/${pick.sport}/game/_/gameId/${pick.eventId}`}
                  target={'_tab'}
                  className={'pick-detail__body-header-name'}
                >
                  {pick.gameName}
                </a>
              )}
              <div className={'pick-detail__body-header-info'}>
                <div>
                  {pick && <span>{new Date(pick.gameStart).toLocaleString()}</span>}
                  <span className={'pick-detail__date'}>
                    Pick Last Updated: {computedDateString}
                  </span>
                </div>
                <div className={'pick-detail__header-middle'}>
                  {pick && <span>{pick.sport.toUpperCase()}</span>}
                </div>
              </div>
            </div>
            <div className={'pickCard__legs'}>
              {pick.legs.map((leg) => (
                <PickLeg key={leg.legIndex} leg={leg} pick={pick} isDetail />
              ))}
            </div>
            <div className={'pick-detail__outcome'}>
              <div className={'pick-detail__pane-data-footer'}>
                <div className={'bet'}>
                  <span>{`${t('pages.picks.game.picks.amount')}`}</span>
                  <Input
                    id={'betCalcInput'}
                    value={betAmount}
                    width={20}
                    onChange={(event) => onBetChangeHandler(parseInt(event?.currentTarget?.value))}
                  />
                </div>
                <div className={'amount'}>
                  <div>
                    <span>{`${t('pages.picks.game.picks.earnings')}`}</span>
                    <span className={'bold'}> ${payoutAmount - betAmount}</span>
                  </div>
                  <div>
                    <span>{`${t('pages.picks.game.picks.payout')}`}</span>
                    <span className={'bold'}> ${payoutAmount}</span>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {!loading && !error && !pick && (
        <div className={'pick-detail__no-results'}>{`${t('pages.picks.noResults')}`}</div>
      )}
    </div>
  );
}

export default PickDetail;
